import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { getAllVehicles } from "@/services/vehicleService";
import { getQuoteById, createQuote, updateQuote } from "@/services/quoteService";
import type { QuoteItem } from "@/types/quote";
import type { Vehicle, Brand } from "@oriental-ramirez/shared";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Search,
  Save,
  Send,
  User,
  ShoppingCart,
  CreditCard,
  StickyNote,
} from "lucide-react";
import { toast } from "sonner";
import { queryKeys } from "@/lib/queryKeys";
import { useAuth } from "@/hooks/useAuth";

const FINANCING_TERMS = [12, 24, 36, 48, 60];

const schema = z.object({
  customerName: z.string().min(1, "Nombre del cliente requerido"),
  customerEmail: z.string().email("Email inválido"),
  customerPhone: z.string().min(1, "Teléfono requerido"),
  customerAddress: z.string().optional(),
  customerType: z.enum(["individual", "business"]),
  company: z.string().optional(),
  taxId: z.string().optional(),
  notes: z.string().optional(),
  internalNotes: z.string().optional(),
  validUntil: z.string().optional(),
  downPayment: z.union([z.coerce.number(), z.nan()]).optional(),
  financingTerm: z.string().optional(),
  interestRate: z.union([z.coerce.number(), z.nan()]).optional(),
});

type FormData = z.infer<typeof schema>;

const TAX_RATE = 0.18;

export function QuoteForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const [items, setItems] = useState<QuoteItem[]>([]);
  const [vehicleSearch, setVehicleSearch] = useState("");
  const [showVehicleSearch, setShowVehicleSearch] = useState(false);
  const [enableFinancing, setEnableFinancing] = useState(false);
  const [saving, setSaving] = useState(false);

  const { data: vehicles = [] } = useQuery({
    queryKey: queryKeys.vehicles,
    queryFn: getAllVehicles,
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      customerType: "individual",
      financingTerm: "",
    },
  });

  const customerType = watch("customerType");
  const downPayment = watch("downPayment");
  const financingTerm = watch("financingTerm");
  const interestRate = watch("interestRate");

  // Load existing quote for edit
  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const quote = await getQuoteById(id);
        if (!quote) {
          toast.error("Cotización no encontrada");
          navigate("/quotes");
          return;
        }
        const q = quote as Record<string, unknown>;
        const customer = q.customer as Record<string, string> | undefined;
        reset({
          customerName: customer?.name ?? "",
          customerEmail: customer?.email ?? "",
          customerPhone: customer?.phone ?? "",
          customerAddress: customer?.address ?? "",
          customerType: (customer?.customerType as "individual" | "business") ?? "individual",
          company: customer?.company ?? "",
          taxId: customer?.taxId ?? "",
          notes: (q.notes as string) ?? "",
          internalNotes: (q.internalNotes as string) ?? "",
          validUntil: "",
        });
        setItems((q.items as QuoteItem[]) ?? []);
        const financing = q.financing as Record<string, number> | undefined;
        if (financing) {
          setEnableFinancing(true);
          setValue("downPayment", financing.downPayment ?? 0);
          setValue("financingTerm", String(financing.financingTerm ?? ""));
          setValue("interestRate", financing.interestRate ?? 0);
        }
      } catch {
        toast.error("Error al cargar la cotización");
        navigate("/quotes");
      }
    })();
  }, [id, navigate, reset, setValue]);

  // Auto-calculate totals
  const subtotal = useMemo(() => items.reduce((sum, i) => sum + i.totalPrice, 0), [items]);
  const taxAmount = useMemo(() => Math.round(subtotal * TAX_RATE), [subtotal]);
  const totalAmount = useMemo(() => subtotal + taxAmount, [subtotal, taxAmount]);

  // Financing calculation
  const monthlyPayment = useMemo(() => {
    if (!enableFinancing || !financingTerm) return 0;
    const term = Number(financingTerm);
    const dp = Number(downPayment) || 0;
    const rate = (Number(interestRate) || 0) / 100 / 12;
    const principal = totalAmount - dp;
    if (principal <= 0 || term <= 0) return 0;
    if (rate === 0) return Math.round(principal / term);
    return Math.round((principal * rate * Math.pow(1 + rate, term)) / (Math.pow(1 + rate, term) - 1));
  }, [enableFinancing, financingTerm, downPayment, interestRate, totalAmount]);

  // Vehicle search filter
  const filteredVehicles = useMemo(() => {
    if (!vehicleSearch.trim()) return vehicles.slice(0, 20);
    const s = vehicleSearch.toLowerCase().trim();
    return vehicles
      .filter(
        (v) =>
          v.name?.toLowerCase().includes(s) ||
          v.model?.toLowerCase().includes(s) ||
          (v.brand as Brand)?.name?.toLowerCase().includes(s)
      )
      .slice(0, 20);
  }, [vehicles, vehicleSearch]);

  const addVehicleItem = (vehicle: Vehicle) => {
    const existing = items.find((i) => i.vehicleId === vehicle.id);
    if (existing) {
      setItems((prev) =>
        prev.map((i) =>
          i.vehicleId === vehicle.id
            ? { ...i, quantity: i.quantity + 1, totalPrice: (i.quantity + 1) * i.unitPrice }
            : i
        )
      );
    } else {
      const price = vehicle.salePrice ?? vehicle.minPrice ?? vehicle.initPrice ?? 0;
      setItems((prev) => [
        ...prev,
        {
          vehicleId: vehicle.id,
          vehicleName: vehicle.name ?? vehicle.model ?? "Sin nombre",
          vehicleBrand: (vehicle.brand as Brand)?.name ?? "",
          vehicleImage: vehicle.images?.[0] ?? "",
          quantity: 1,
          unitPrice: price,
          totalPrice: price,
        },
      ]);
    }
    setShowVehicleSearch(false);
    setVehicleSearch("");
  };

  const updateItemQuantity = (vehicleId: string, quantity: number) => {
    if (quantity < 1) return;
    setItems((prev) =>
      prev.map((i) =>
        i.vehicleId === vehicleId ? { ...i, quantity, totalPrice: quantity * i.unitPrice } : i
      )
    );
  };

  const updateItemPrice = (vehicleId: string, unitPrice: number) => {
    setItems((prev) =>
      prev.map((i) =>
        i.vehicleId === vehicleId ? { ...i, unitPrice, totalPrice: i.quantity * unitPrice } : i
      )
    );
  };

  const removeItem = (vehicleId: string) => {
    setItems((prev) => prev.filter((i) => i.vehicleId !== vehicleId));
  };

  const formatCurrency = (amount: number): string =>
    new Intl.NumberFormat("es-DO", { style: "currency", currency: "DOP", minimumFractionDigits: 0 }).format(amount);

  const onSubmit = async (data: FormData, status: "DRAFT" | "SENT") => {
    if (items.length === 0) {
      toast.error("Agrega al menos un vehículo a la cotización");
      return;
    }

    setSaving(true);
    try {
      const quoteData: Record<string, unknown> = {
        customer: {
          name: data.customerName,
          email: data.customerEmail,
          phone: data.customerPhone,
          address: data.customerAddress ?? "",
          customerType: data.customerType,
          company: data.company ?? "",
          taxId: data.taxId ?? "",
        },
        items,
        subtotal,
        taxRate: TAX_RATE,
        taxAmount,
        totalAmount,
        notes: data.notes ?? "",
        internalNotes: data.internalNotes ?? "",
        validUntil: data.validUntil ? new Date(data.validUntil) : null,
        status,
        sentToCustomer: status === "SENT",
        convertedToSale: false,
        createdBy: user?.uid ?? "",
      };

      if (enableFinancing && financingTerm) {
        quoteData.financing = {
          downPayment: Number(data.downPayment) || 0,
          financingTerm: Number(data.financingTerm),
          interestRate: Number(data.interestRate) || 0,
          monthlyPayment,
          totalFinanced: totalAmount - (Number(data.downPayment) || 0),
        };
      }

      if (isEdit && id) {
        await updateQuote(id, quoteData);
        toast.success("Cotización actualizada");
      } else {
        const result = await createQuote(quoteData);
        toast.success(`Cotización ${result.quoteNumber} creada`);
      }

      await queryClient.invalidateQueries({ queryKey: queryKeys.quotes });
      navigate("/quotes");
    } catch {
      toast.error("Error al guardar la cotización");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/quotes">
            <ArrowLeft className="h-4 w-4" />
            <span className="sr-only">Volver</span>
          </Link>
        </Button>
        <h1 className="font-display text-2xl font-semibold">
          {isEdit ? "Editar Cotización" : "Nueva Cotización"}
        </h1>
      </div>

      <form
        onSubmit={handleSubmit((data) => onSubmit(data, "DRAFT"))}
        className="space-y-6"
      >
        {/* Customer Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Cliente
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="customerType">Tipo de cliente</Label>
                <Select
                  value={customerType}
                  onValueChange={(v) => setValue("customerType", v as "individual" | "business")}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="individual">Persona Física</SelectItem>
                    <SelectItem value="business">Empresa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="customerName">Nombre *</Label>
                <Input id="customerName" {...register("customerName")} placeholder="Nombre completo" />
                {errors.customerName && (
                  <p className="text-sm text-destructive">{errors.customerName.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="customerEmail">Email *</Label>
                <Input id="customerEmail" type="email" {...register("customerEmail")} placeholder="correo@ejemplo.com" />
                {errors.customerEmail && (
                  <p className="text-sm text-destructive">{errors.customerEmail.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="customerPhone">Teléfono *</Label>
                <Input id="customerPhone" {...register("customerPhone")} placeholder="809-000-0000" />
                {errors.customerPhone && (
                  <p className="text-sm text-destructive">{errors.customerPhone.message}</p>
                )}
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="customerAddress">Dirección</Label>
                <Input id="customerAddress" {...register("customerAddress")} placeholder="Dirección del cliente" />
              </div>
              {customerType === "business" && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="company">Empresa</Label>
                    <Input id="company" {...register("company")} placeholder="Nombre de la empresa" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="taxId">RNC / Cédula</Label>
                    <Input id="taxId" {...register("taxId")} placeholder="RNC o cédula" />
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Items Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" />
              Vehículos
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Vehicle search */}
            <div className="relative">
              <Button
                type="button"
                variant="outline"
                className="gap-2"
                onClick={() => setShowVehicleSearch(!showVehicleSearch)}
              >
                <Plus className="h-4 w-4" />
                Agregar Vehículo
              </Button>
              {showVehicleSearch && (
                <div className="mt-2 rounded-md border bg-background p-4 shadow-md">
                  <div className="relative mb-3">
                    <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder="Buscar vehículo por nombre, modelo o marca..."
                      value={vehicleSearch}
                      onChange={(e) => setVehicleSearch(e.target.value)}
                      className="pl-8"
                      autoFocus
                    />
                  </div>
                  <div className="max-h-60 overflow-auto">
                    {filteredVehicles.length === 0 ? (
                      <p className="py-4 text-center text-sm text-muted-foreground">
                        No se encontraron vehículos
                      </p>
                    ) : (
                      filteredVehicles.map((v) => (
                        <button
                          key={v.id}
                          type="button"
                          className="flex w-full items-center gap-3 rounded p-2 text-left hover:bg-muted"
                          onClick={() => addVehicleItem(v)}
                        >
                          {v.images?.[0] ? (
                            <img
                              src={v.images[0]}
                              alt=""
                              className="h-10 w-10 rounded object-cover border border-border"
                            />
                          ) : (
                            <div className="flex h-10 w-10 items-center justify-center rounded border border-dashed border-muted-foreground/30 bg-muted/50 text-xs text-muted-foreground">
                              --
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="truncate font-medium text-sm">{v.name ?? v.model ?? "Sin nombre"}</p>
                            <p className="text-xs text-muted-foreground">
                              {(v.brand as Brand)?.name ?? ""} {v.year ? `· ${v.year}` : ""}
                            </p>
                          </div>
                          <span className="text-sm font-medium">
                            {formatCurrency(v.salePrice ?? v.minPrice ?? v.initPrice ?? 0)}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="mt-2 w-full"
                    onClick={() => {
                      setShowVehicleSearch(false);
                      setVehicleSearch("");
                    }}
                  >
                    Cerrar
                  </Button>
                </div>
              )}
            </div>

            {/* Items table */}
            {items.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Vehículo</TableHead>
                    <TableHead className="w-[100px]">Cantidad</TableHead>
                    <TableHead className="w-[150px]">Precio Unit.</TableHead>
                    <TableHead className="w-[130px]">Total</TableHead>
                    <TableHead className="w-[60px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item.vehicleId}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {item.vehicleImage ? (
                            <img
                              src={item.vehicleImage}
                              alt=""
                              className="h-8 w-8 rounded object-cover border border-border"
                            />
                          ) : null}
                          <div>
                            <p className="font-medium text-sm">{item.vehicleName}</p>
                            {item.vehicleBrand && (
                              <p className="text-xs text-muted-foreground">{item.vehicleBrand}</p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          min={1}
                          value={item.quantity}
                          onChange={(e) => updateItemQuantity(item.vehicleId, Number(e.target.value))}
                          className="w-20"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          min={0}
                          value={item.unitPrice}
                          onChange={(e) => updateItemPrice(item.vehicleId, Number(e.target.value))}
                          className="w-36"
                        />
                      </TableCell>
                      <TableCell className="font-medium">{formatCurrency(item.totalPrice)}</TableCell>
                      <TableCell>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeItem(item.vehicleId)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="py-8 text-center text-muted-foreground">
                No hay vehículos agregados. Usa el botón de arriba para agregar.
              </p>
            )}

            {/* Totals */}
            {items.length > 0 && (
              <div className="flex justify-end">
                <div className="w-64 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span className="font-medium">{formatCurrency(subtotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">ITBIS (18%)</span>
                    <span className="font-medium">{formatCurrency(taxAmount)}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2 text-base font-semibold">
                    <span>Total</span>
                    <span>{formatCurrency(totalAmount)}</span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Financing Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Financiamiento
              <label className="ml-auto flex items-center gap-2 text-sm font-normal">
                <input
                  type="checkbox"
                  checked={enableFinancing}
                  onChange={(e) => setEnableFinancing(e.target.checked)}
                  className="rounded"
                />
                Incluir financiamiento
              </label>
            </CardTitle>
          </CardHeader>
          {enableFinancing && (
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="downPayment">Inicial (DOP)</Label>
                  <Input
                    id="downPayment"
                    type="number"
                    min={0}
                    {...register("downPayment")}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="financingTerm">Plazo (meses)</Label>
                  <Select
                    value={financingTerm ?? ""}
                    onValueChange={(v) => setValue("financingTerm", v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar plazo" />
                    </SelectTrigger>
                    <SelectContent>
                      {FINANCING_TERMS.map((t) => (
                        <SelectItem key={t} value={String(t)}>
                          {t} meses
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="interestRate">Tasa de interés (%)</Label>
                  <Input
                    id="interestRate"
                    type="number"
                    step="0.01"
                    min={0}
                    {...register("interestRate")}
                    placeholder="0"
                  />
                </div>
              </div>
              {monthlyPayment > 0 && (
                <div className="rounded-md bg-muted p-4">
                  <p className="text-sm text-muted-foreground">Cuota mensual estimada</p>
                  <p className="text-2xl font-semibold">{formatCurrency(monthlyPayment)}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Total financiado: {formatCurrency(totalAmount - (Number(downPayment) || 0))}
                  </p>
                </div>
              )}
            </CardContent>
          )}
        </Card>

        {/* Notes Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <StickyNote className="h-5 w-5" />
              Notas
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="notes">Notas para el cliente</Label>
                <Textarea
                  id="notes"
                  {...register("notes")}
                  placeholder="Notas visibles para el cliente..."
                  rows={4}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="internalNotes">Notas internas</Label>
                <Textarea
                  id="internalNotes"
                  {...register("internalNotes")}
                  placeholder="Notas internas (no visibles para el cliente)..."
                  rows={4}
                />
              </div>
            </div>
            <div className="space-y-2 sm:max-w-xs">
              <Label htmlFor="validUntil">Válido hasta</Label>
              <Input id="validUntil" type="date" {...register("validUntil")} />
            </div>
          </CardContent>
        </Card>

        {/* Action buttons */}
        <div className="flex flex-wrap gap-3 justify-end">
          <Button type="button" variant="outline" asChild>
            <Link to="/quotes">Cancelar</Link>
          </Button>
          <Button
            type="submit"
            variant="outline"
            disabled={saving}
            className="gap-2"
          >
            <Save className="h-4 w-4" />
            {saving ? "Guardando..." : "Guardar borrador"}
          </Button>
          <Button
            type="button"
            disabled={saving}
            className="gap-2"
            onClick={handleSubmit((data) => onSubmit(data, "SENT"))}
          >
            <Send className="h-4 w-4" />
            {saving ? "Enviando..." : "Guardar y Enviar"}
          </Button>
        </div>
      </form>
    </div>
  );
}
